import { useCallback } from 'react'
import { gql } from '@apollo/client'
import { useTranslations } from '@npco/utils-translations'
import {
  ButtonFill,
  ButtonLink,
  Flex,
  Tutorial,
} from '@npco/zeller-design-system'
import { useGoToCreateSavingsAccount } from 'features/CreateSavingsAccount/create-savings-account-routing'
import { useFeatureFlags } from 'features/FeatureFlags'

import { useCustomerProductTourStatus } from 'hooks/useCustomerProductTourStatus'
import { DebitCardAccountStatus } from 'types/gql-global-types'
import { useGoToInternalTransfer } from 'pages/Transfer/hooks/useGoToInternalTransfer/useGoToInternalTransfer'
import { translationsShared } from 'translations'

import { useGoToBlockedAction } from '../../../BlockedAction/blocked-action-routing'
import { ImageContainer } from '../../SetupFlow/components/ImageContainer/ImageContainer'
import * as styled from '../../StartupModals.styled'
import { getSavingsStartupMarchImage } from '../SavingsStartupMarch.utils'
import { ModalContent } from './ModalContent/ModalContent'
import {
  SavingsStartupModalMarchDebitCardAccountV2Fragment,
  SavingsStartupModalMarchEntityFragment,
} from './SavingsStartupModalMarch.generated'
import { savingsStartupModalMarchTranslations } from './SavingsStartupModalMarch.i18n'

type SavingsStartupModalMarchProps = {
  savingsAccount: SavingsStartupModalMarchDebitCardAccountV2Fragment | null
  entity: SavingsStartupModalMarchEntityFragment
  isOpen: boolean
  setIsOpen: (newIsOpen: boolean) => void
}

export const SavingsStartupModalMarch = ({
  savingsAccount,
  entity,
  isOpen,
  setIsOpen,
}: SavingsStartupModalMarchProps) => {
  const { goToCreateSavingsAccount } = useGoToCreateSavingsAccount()
  const { updateProductTourStatus } = useCustomerProductTourStatus()
  const { goToBlockedAction } = useGoToBlockedAction()
  const { goToInternalTransfer } = useGoToInternalTransfer()

  const isSavingsAccountActivated =
    savingsAccount?.status === DebitCardAccountStatus.ACTIVE

  const closeModal = useCallback(() => {
    updateProductTourStatus({
      showSavingsAccountWelcome: false,
      showSavingsAccountMarch: false,
    })
    setIsOpen(false)
  }, [updateProductTourStatus, setIsOpen])

  const flags = useFeatureFlags()
  const goToActivateSavingsAccount = useCallback(() => {
    closeModal()

    const isCreateAccountBlocked =
      flags.AmsFlags && entity?.canCreateAccount === false

    if (isCreateAccountBlocked) {
      goToBlockedAction({
        blockedAction: 'createAccount',
        hasBlurAndFade: true,
      })
      return
    }

    goToCreateSavingsAccount({ stage: 'tfn', hasBlurAndFade: true })
  }, [
    closeModal,
    entity?.canCreateAccount,
    goToCreateSavingsAccount,
    goToBlockedAction,
    flags.AmsFlags,
  ])

  const goToTransfer = useCallback(() => {
    closeModal()

    goToInternalTransfer({
      transferType: 'internal',
      from: '',
      to: savingsAccount?.id ?? '',
    })
  }, [closeModal, goToInternalTransfer, savingsAccount?.id])

  const tShared = useTranslations(translationsShared)
  const t = useTranslations(savingsStartupModalMarchTranslations)

  return (
    <styled.CustomizedModal
      className="animated-content"
      closeTimeoutMS={350}
      isOpen={isOpen}
      onCancel={closeModal}
      overlayClassName="animated-overlay"
      shouldCloseOnEsc={false}
      shouldCloseOnOverlayClick={false}
    >
      <Tutorial.TutorialLayout
        Footer={
          <Flex
            justifyContent="space-between"
            padding={{
              _: '12px 24px 24px',
              SM: '12px 32px 32px',
              LG: '12px 0 0 0',
            }}
            width="100%"
          >
            <ButtonLink onClick={closeModal}>{tShared('close')}</ButtonLink>
            {isSavingsAccountActivated ? (
              <ButtonFill onClick={goToTransfer}>{t('addFunds')}</ButtonFill>
            ) : (
              <ButtonFill onClick={goToActivateSavingsAccount}>
                {t('activate')}
              </ButtonFill>
            )}
          </Flex>
        }
        Illustration={
          <ImageContainer
            mobileImagePath={
              getSavingsStartupMarchImage({
                isMobile: true,
                isMobileOrTablet: true,
              })[0]
            }
            tabletImagePath={
              getSavingsStartupMarchImage({
                isMobile: false,
                isMobileOrTablet: true,
              })[0]
            }
            desktopImagePath={
              getSavingsStartupMarchImage({
                isMobile: false,
                isMobileOrTablet: false,
              })[0]
            }
          />
        }
        MainContent={
          entity.savingsAccountProduct && (
            <ModalContent
              entitySavingsAccountProduct={entity.savingsAccountProduct}
              isSavingsAccountActivated={isSavingsAccountActivated}
            />
          )
        }
      />
    </styled.CustomizedModal>
  )
}

SavingsStartupModalMarch.fragments = {
  Entity: gql`
    fragment SavingsStartupModalMarchEntityFragment on Entity {
      canCreateAccount
      savingsAccountProduct {
        ...ModalContentEntitySavingsAccountProductFragment
      }
    }

    ${ModalContent.fragments.EntitySavingsAccountProduct}
  `,
  DebitCardAccountV2: gql`
    fragment SavingsStartupModalMarchDebitCardAccountV2Fragment on DebitCardAccountV2 {
      id
      status
    }
  `,
}
